<template>
	<div class="d21-shoppingCart" v-if="isEnabledForMode">
		<button class="btn btn-primary" @click="addToShoppingCart" :disabled="isDisabled">
			<span>{{ T("book_another_ticket") }}</span>
		</button>
		<button class="btn d21-shoppingCart-icon" v-if="Reservation.MultiDate.IsActive" @click="$modals.show('shopping-cart')">
			<i class="material-icons">shopping_cart</i>
			<span>{{ shoppingCartCount }}</span>
		</button>
		<aside>
			<Modal name="shopping-cart" :title="T('shopping_cart')" :titleInfo="shoppingCartTotal" :subtitle="T('shopping_cart_blocked_for', formatExpires(expiresInMs))" :actions="shoppingCartModalActions">
				<div class="mdl-grid">
					<div class="mdl-cell mdl-cell--12-col d21-modal-shoppingCart-set" v-for="(set, setX) in Reservation.MultiDate.Sets" :key="'set_' + setX">
						<div class="mdl-grid">
							<div class="mdl-cell mdl-cell--11-col d21-modal-shoppingCart-setName d-flex d-flex-mobile">
								<div>{{ set.Content.Name }}</div>
								<div>{{ formatPrice(set.Sum.Total) }}</div>
							</div>
							<div class="mdl-cell mdl-cell--1-col d21-modal-shoppingCart-setName"><i class="material-icons" @click="openRemoveConfirmModal(setX)">delete</i></div>
							<div class="mdl-cell mdl-cell--12-col">
								<div class="mdl-grid" v-for="(product, productX) in set.Products" :key="'product_' + setX + '_' + productX">
									<div class="mdl-cell mdl-cell--12-col">
										{{ product.Count }}x {{ product.Content.Name }}
									</div>
								</div>
							</div>
						</div>
						<hr v-if="setX !== Reservation.MultiDate.Sets.length - 1" />
					</div>
				</div>
			</Modal>
			<Modal name="shopping-cart.remove-confirm" :title="T('ticketing_shopping_cart_remove_confirm')" :actions="removeConfirmationModalActions">
				<div class="mdl-grid">
					<span>{{ T("ticketing_shopping_cart_remove_confirm_message") }}</span>
				</div>
			</Modal>
		</aside>
	</div>
</template>

<script>
	import baseMixin from "../mixins/base";
	import moment from "moment";
	import api from "../scripts/_api";
	import {
		EventBus
	} from "../scripts/_eventBus.js";
	import Modal from './modal.vue';

	// only enabled for shifts for now, there are still some issues that products cannot be found when switching modes, 
	// because in Reservation.Info only are the products of the current selected mode
	// but this component should be able to handle Events and Seasonal products already.
	const enabledModes = ["Shifts", "Dates"];

	export default {
		mixins: [baseMixin],
		props: {
			modalWidth: {
				type: String,
				default: '90%',
			}
		},
		components: {
			Modal
		},
		data() {
			return {
				expiresInMs: null,
				countdownInterval: null,
				worker: null,
				selectedSetX: -1
			}
		},
		mounted() {
			this.initWorker();
			this.initCountdown();
		},
		methods: {
			next() {
				this.$modals.hide('shopping-cart');
				EventBus.$emit("next");
			},
			initWorker() {
				this.worker = new Worker("/scripts/_worker.js");
				this.worker.onmessage = this.handleWorkerMessage;
			},
			handleWorkerMessage(e) {
				if (e.data && e.data.expiresInMs) {
					this.expiresInMs = e.data.expiresInMs;
				}
			},
			onModalOpened() {
				document.querySelector("html").style.overflow = "hidden";
			},
			onModalClosed() {
				document.querySelector("html").style.overflow = "auto";
			},
			initCountdown() {
				if (this.Reservation._temp) {
					// worker will handle the calculation of expiry					
					this.worker.postMessage({
						expiry: this.Reservation._temp.expiry
					});
				}
			},
			openRemoveConfirmModal(setX) {
				var mediaQuery = window.matchMedia("(max-width: 700px)");
				if (mediaQuery.matches) {
					this.selectedSetX = setX;
					this.$modals.show("shopping-cart.remove-confirm");
					return;
				}
				this.removeFromShoppingCart(setX);
			},
			removeFromShoppingCart(setX) {
				this.$modals.hide('shopping-cart.remove-confirm');
				const reservation = this.Reservation.MultiDate.Sets[setX];
				this.Reservation.MultiDate.Sets.splice(setX, 1);
				this.Reservation.MultiDate.Sum.Total -= reservation.Sum.Total;
				if (this.Reservation.MultiDate.Sets.length === 0) {
					this.$modals.hide('shopping-cart');
					this.Reservation.MultiDate.IsActive = false;
				}

				// find blocked capacities for all products in the set that will be deleted.
				const blockedCapacitySet = this.Reservation._temp.blockedCapacities[setX];

				blockedCapacitySet.forEach(capacity => {
					this.removeCapacity(capacity);
				});

				this.selectedSetX = -1;
				this.Reservation._temp.blockedCapacities.splice(setX, 1);
			},
			addToShoppingCart() {
				if (this.isEnabledForMode) {
					const selectedProducts = this.Reservation.Products.filter(p => p.Tariffs.some(t => t.Count > 0));
					if (selectedProducts.length === 0) return;

					this.Reservation.MultiDate.IsActive = true;
					const set = {
						Period: {
							From: selectedProducts[0].Period.From,
							Until: selectedProducts[0].Period.Until,
						},
						Content: {
							Name: "",
						},
						Products: [],
						Sum: {
							Total: 0
						}
					};

					let sum = 0;
					selectedProducts.forEach(p => {
						const selectedTariffs = p.Tariffs.filter(t => t.Count > 0);
						// calc total based on selected tariffs of this product
						sum += selectedTariffs.reduce((acc, tariff) => acc + tariff.Total * tariff.Count, 0);
						const product = this.Reservation.fn.getProduct(p.Product);

						selectedTariffs.forEach(tariff => {
							const t = this.Reservation.fn.getTariff(tariff.Tariff);

							let displayName;
							if (["Events", "Seasonal"].includes(product.Settings.ScheduleType)) {
								displayName = product.Content.Name + " - " + t.Content.Name;
							} else {
								displayName = this.selectedTimeFrame.text + " - " + t.Content.Name;
							}

							const clone = JSON.parse(JSON.stringify(p));

							let totalNumberOfPersons;

							if (tariff.Units.length) {
								// sum of all Units
								totalNumberOfPersons = tariff.Units.reduce((acc, unit) => acc + unit, 0);
							} else {
								// count
								totalNumberOfPersons = tariff.Count;
							}

							clone.Content = {
								Name: displayName,
							};
							clone.Price = {
								Value: tariff.Total,
							};
							clone.Count = tariff.Count;
							clone.Tariff = tariff.Tariff;
							clone.NumberOfPersons = totalNumberOfPersons;
							clone.Tariffs = [];

							// Create occupancy array for the tariff
							// Handle per units
							if (tariff.Units.length) {
								tariff.Units.forEach(unit => {
									const tariffClone = JSON.parse(JSON.stringify(tariff));
									tariffClone.Count = 1;
									tariffClone.NumberOfPersons = unit;
									tariffClone.Occupancy = [];
									// Each unit counts as a product
									for (var i = 0; i < unit; i++) {
										tariffClone.Occupancy.push({
											Person: {
												Givenname: "",
												Lastname: "",
												Communication: {
													Email: null,
													Address: null,
													PostalCode: null,
													City: null
												}
											}
										});
									}
									clone.Tariffs.push(tariffClone);
								})
							} else {
								const tariffClone = JSON.parse(JSON.stringify(tariff));
								clone.Tariffs.push(tariffClone);
							}
							set.Products.push(clone);
						});
					});

					var firstProduct = this.Reservation.fn.getProduct(selectedProducts[0].Product);
					if (["Events", "Seasonal"].includes(firstProduct.Settings.ScheduleType)) {
						set.Content.Name = firstProduct.Content.Name;
					} else {
						const shiftName = this.selectedTimeFrame.text;
						set.Content.Name = `${moment(selectedProducts[0].Period.From).format("dddd DD.MM.YYYY")}, ${shiftName} (${moment(selectedProducts[0].Period.From).format("HH:mm")}-${moment(selectedProducts[0].Period.Until).format("HH:mm")} Uhr)`;
					}
					set.Sum.Total = sum;
					this.Reservation.MultiDate.Sets.push(set);
					this.Reservation.MultiDate.Sum.Total += set.Sum.Total;
					this.blockCapacities();
				}
			},
			removeCapacity(capacity) {
				const command = `BlockedCapacities/${capacity._uuid}`;
				api.delete(command, response => {
					if (response.Value) {
						// Do nothing.
					}
				});
			},
			formatExpires(val) {
				const duration = moment.duration(val);
				return `${duration.minutes().toString().padStart(2, '0')}:${duration.seconds().toString().padStart(2, '0')}`
			},
			blockCapacities() {
				let command = `BlockedCapacities`;
				const blockedCapacities = [];
				// map model for api request
				this.Reservation.MultiDate.Sets.forEach((set, setX) => {
					const existing = this.Reservation._temp &&
						this.Reservation._temp.blockedCapacities.length - 1 >= setX ? this.Reservation._temp.blockedCapacities[setX] : null;

					const models = set.Products.map((product, productX) => {
						const existingBlockedCapacity = existing && existing.length - 1 >= productX ? existing[productX] : null;
						return {
							...existingBlockedCapacity,
							ProductUuid: product.Product,
							TenantUuid: product.Tariff,
							TickStart: product.Period.From,
							TickEnd: product.Period.Until,
							Value: product.NumberOfPersons,
							_oid: this.Channel._oid,
						};
					});
					blockedCapacities.push(...models);
				});

				// Set session token if we have one -> to refresh
				if (this.Reservation._temp && this.Reservation._temp.token) {
					blockedCapacities.forEach(c => c.Token = this.Reservation._temp.token);
				}

				EventBus.$emit("loading_start");
				api.post(command, blockedCapacities, response => {
					if (response.Value && response.Value.length) {
						// capacities are blocked now. Set the token and expiry.
						const res = response.Value[0];
						const model = {
							token: res.Token,
							expiry: res.Expiry,
							blockedCapacities: response.Value,
						};

						if (!this.Reservation._temp) {
							this.Reservation._temp = {
								blockedCapacities: [],
							}
						}

						// get new blocked capacities that have not been added yet. (counts as "set").
						const blockedCapacitySet = model.blockedCapacities.filter(c => !this.Reservation._temp.blockedCapacities.flat().some(d => d._uuid === c._uuid));
						// add new blocked capacity set
						const blockedCapacities = [...this.Reservation._temp.blockedCapacities]
						blockedCapacities.push(blockedCapacitySet);

						this.$set(this.Reservation, "_temp", {
							...model,
							blockedCapacities
						});

						this.initCountdown();
					}
					this.$emit("add");
				});
			}
		},
		computed: {
			shoppingCartModalActions() {
				return [{
					text: this.T('close'),
					onClick: () => {
						this.$modals.hide('shopping-cart');
					}
				}, {
					text: this.T('proceed'),
					onClick: () => {
						this.next();
					},
					isPrimary: true
				}];
			},
			removeConfirmationModalActions() {
				return [{
					text: this.T('no'),
					onClick: () => {
						this.$modals.hide('shopping-cart.remove-confirm');
					}
				}, {
					text: this.T('yes'),
					onClick: () => {
						this.removeFromShoppingCart(this.selectedSetX);
					},
					isPrimary: true
				}];
			},
			shoppingCartCount() {
				if (!this.Reservation.MultiDate.IsActive) return 0;
				return this.Reservation.MultiDate.Sets.length;
			},
			shoppingCartTotal() {
				let total = 0;
				this.Reservation.MultiDate.Sets.forEach(e => total += e.Sum.Total);
				return this.formatPrice(total);
			},
			isTimerVisible() {
				return this.expiresInMs && this.expiresInMs > 0;
			},
			isTimerExpired() {
				return this.expiresInMs != null && this.expiresInMs <= 0;
			},
			isDisabled() {
				return this.Reservation.Products.every(p => p.Tariffs.every(t => t.Count === 0));
			},
			isEnabledForMode() {
				return enabledModes.includes(this.Reservation.Mode);
			}
		}
	}
</script>